/**
 * MEVIA - Subscription Management Panel
 * SuperAdmin component for managing subscription plans and company subscriptions
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Settings, 
  Building, 
  CreditCard, 
  Users, 
  Check, 
  X, 
  Edit, 
  Plus,
  Save,
  Trash2,
  ChevronDown,
  ChevronUp,
  Calendar,
  AlertCircle,
  CheckCircle,
  Loader2,
  Eye,
  Shield,
  BarChart3,
  FileText,
  Zap,
  Clock,
  DollarSign
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import logger from '../utils/logger';

const SubscriptionManagement = () => {
  const { language } = useLanguage();
  const [activeTab, setActiveTab] = useState('plans'); // 'plans' or 'companies'
  const [plans, setPlans] = useState([]);
  const [companySubscriptions, setCompanySubscriptions] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPlan, setEditingPlan] = useState(null);
  const [showPlanForm, setShowPlanForm] = useState(false);
  const [showAssignForm, setShowAssignForm] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState(null);
  const [showEditSubscriptionForm, setShowEditSubscriptionForm] = useState(false);
  const [assignFormData, setAssignFormData] = useState({
    empresa_id: '',
    plan_id: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    is_trial: false,
    trial_ends_at: '',
    notes: ''
  });

  const t = {
    es: {
      title: 'Gestión de Suscripciones',
      plans: 'Planes',
      companies: 'Empresas',
      planName: 'Nombre del Plan',
      description: 'Descripción',
      priceMonthly: 'Precio Mensual (MXN)',
      priceAnnual: 'Precio Anual (MXN)',
      maxEvaluations: 'Máx. Evaluaciones',
      maxEmployees: 'Máx. Empleados',
      unlimited: 'Ilimitado',
      modules: 'Módulos',
      features: 'Características',
      active: 'Activo',
      inactive: 'Inactivo',
      save: 'Guardar',
      cancel: 'Cancelar',
      edit: 'Editar',
      delete: 'Eliminar',
      newPlan: 'Nuevo Plan',
      assignSubscription: 'Asignar Suscripción',
      company: 'Empresa',
      plan: 'Plan',
      startDate: 'Fecha Inicio',
      endDate: 'Fecha Fin',
      trial: 'Prueba',
      trialEnds: 'Fin de Prueba',
      notes: 'Notas',
      evaluationsUsed: 'Evaluaciones Usadas',
      status: 'Estado',
      noSubscription: 'Sin suscripción',
      expired: 'Expirado',
      trialExpired: 'Prueba expirada',
      actions: 'Acciones',
      editSubscription: 'Editar Suscripción',
      deleteSubscription: 'Eliminar Suscripción',
      confirmDelete: '¿Estás seguro de eliminar esta suscripción?',
      subscriptionUpdated: 'Suscripción actualizada',
      subscriptionDeleted: 'Suscripción eliminada',
      moduleLabels: {
        culture: 'Cultura',
        personality: 'Personalidad',
        integrated_report: 'Reporte Integrado',
        hr_indicators: 'Indicadores RH',
        alerts: 'Alertas',
        action_plans: 'Planes de Acción',
        evaluation_periods: 'Periodos de Evaluación',
        profile_evaluation: 'Evaluación por Perfiles',
        corporate_identity: 'Identidad Corporativa',
        pdf_export: 'Exportar PDF',
        candidate_evaluation: 'Evaluación Candidatos',
        nom035: 'NOM-035',
        orgchart: 'Organigrama',
        dashboard_central: 'Dashboard Central',
        evolution_impact: 'ROI e Impacto',
        pulse_surveys: 'Encuestas Pulse',
        strategic_plans: 'Planes Estratégicos',
        evaluation_cycles: 'Ciclos de Evaluación',
        company_profile: 'Perfil de Empresa',
        compatibility_4d: 'Compatibilidad 4D'
      },
      featureLabels: {
        basic_reports: 'Reportes Básicos',
        advanced_reports: 'Reportes Avanzados',
        api_access: 'Acceso API',
        priority_support: 'Soporte Prioritario'
      }
    },
    en: {
      title: 'Subscription Management',
      plans: 'Plans',
      companies: 'Companies',
      planName: 'Plan Name',
      description: 'Description',
      priceMonthly: 'Monthly Price (MXN)',
      priceAnnual: 'Annual Price (MXN)',
      maxEvaluations: 'Max Evaluations',
      maxEmployees: 'Max Employees',
      unlimited: 'Unlimited',
      modules: 'Modules',
      features: 'Features',
      active: 'Active',
      inactive: 'Inactive',
      save: 'Save',
      cancel: 'Cancel',
      edit: 'Edit',
      delete: 'Delete',
      newPlan: 'New Plan',
      assignSubscription: 'Assign Subscription',
      company: 'Company',
      plan: 'Plan',
      startDate: 'Start Date',
      endDate: 'End Date',
      trial: 'Trial',
      trialEnds: 'Trial Ends',
      notes: 'Notes',
      evaluationsUsed: 'Evaluations Used',
      status: 'Status',
      noSubscription: 'No subscription',
      expired: 'Expired',
      trialExpired: 'Trial expired',
      actions: 'Actions',
      editSubscription: 'Edit Subscription',
      deleteSubscription: 'Delete Subscription',
      confirmDelete: 'Are you sure you want to delete this subscription?',
      subscriptionUpdated: 'Subscription updated',
      subscriptionDeleted: 'Subscription deleted',
      moduleLabels: {
        culture: 'Culture',
        personality: 'Personality',
        integrated_report: 'Integrated Report',
        hr_indicators: 'HR Indicators',
        alerts: 'Alerts',
        action_plans: 'Action Plans',
        evaluation_periods: 'Evaluation Periods',
        profile_evaluation: 'Profile Evaluation',
        corporate_identity: 'Corporate Identity',
        pdf_export: 'PDF Export',
        candidate_evaluation: 'Candidate Evaluation',
        nom035: 'NOM-035',
        orgchart: 'Org Chart',
        dashboard_central: 'Central Dashboard',
        evolution_impact: 'ROI & Impact',
        pulse_surveys: 'Pulse Surveys',
        strategic_plans: 'Strategic Plans',
        evaluation_cycles: 'Evaluation Cycles',
        company_profile: 'Company Profile',
        compatibility_4d: '4D Compatibility'
      },
      featureLabels: {
        basic_reports: 'Basic Reports',
        advanced_reports: 'Advanced Reports',
        api_access: 'API Access',
        priority_support: 'Priority Support'
      }
    }
  }[language];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [plansRes, subsRes, companiesRes] = await Promise.all([
        axios.get('/subscriptions/plans?include_inactive=true'),
        axios.get('/subscriptions/companies'),
        axios.get('/admin/companies')
      ]);
      
      setPlans(plansRes.data.data.plans || []);
      setCompanySubscriptions(subsRes.data.data.subscriptions || []);
      setCompanies(companiesRes.data.data || companiesRes.data || []);
    } catch (error) {
      logger.error('Error loading subscription data:', error);
      toast.error('Error loading data');
    } finally {
      setLoading(false);
    }
  };

  const handleSavePlan = async (planData) => {
    try {
      if (editingPlan?.id) {
        await axios.put(`/subscriptions/plans/${editingPlan.id}`, planData);
        toast.success('Plan actualizado');
      } else {
        await axios.post('/subscriptions/plans', planData);
        toast.success('Plan creado');
      }
      setEditingPlan(null);
      setShowPlanForm(false);
      loadData();
    } catch (error) {
      toast.error('Error al guardar el plan');
    }
  };

  const handleDeletePlan = async (planId) => {
    if (!confirm('¿Estás seguro de desactivar este plan?')) return;
    try {
      await axios.delete(`/subscriptions/plans/${planId}`);
      toast.success('Plan desactivado');
      loadData();
    } catch (error) {
      toast.error('Error al desactivar el plan');
    }
  };

  const handleAssignSubscription = async () => {
    try {
      await axios.post('/subscriptions/company', {
        ...assignFormData,
        start_date: new Date(assignFormData.start_date).toISOString(),
        end_date: assignFormData.end_date ? new Date(assignFormData.end_date).toISOString() : null,
        trial_ends_at: assignFormData.trial_ends_at ? new Date(assignFormData.trial_ends_at).toISOString() : null
      });
      toast.success('Suscripción asignada');
      setShowAssignForm(false);
      setAssignFormData({
        empresa_id: '',
        plan_id: '',
        start_date: new Date().toISOString().split('T')[0],
        end_date: '',
        is_trial: false,
        trial_ends_at: '',
        notes: ''
      });
      loadData();
    } catch (error) {
      toast.error('Error al asignar suscripción');
    }
  };

  const handleEditSubscription = (subscription) => {
    setEditingSubscription({
      ...subscription,
      plan_id: subscription.plan_id,
      start_date: subscription.start_date ? subscription.start_date.split('T')[0] : '',
      end_date: subscription.end_date ? subscription.end_date.split('T')[0] : '',
      trial_ends_at: subscription.trial_ends_at ? subscription.trial_ends_at.split('T')[0] : '',
      is_trial: subscription.is_trial || false,
      is_active: subscription.is_active ?? true,
      notes: subscription.notes || ''
    });
    setShowEditSubscriptionForm(true);
  };

  const handleUpdateSubscription = async () => {
    try {
      await axios.put(`/subscriptions/company/${editingSubscription.id}`, {
        plan_id: editingSubscription.plan_id,
        is_active: editingSubscription.is_active,
        is_trial: editingSubscription.is_trial,
        start_date: editingSubscription.start_date ? new Date(editingSubscription.start_date).toISOString() : null,
        end_date: editingSubscription.end_date ? new Date(editingSubscription.end_date).toISOString() : null,
        trial_ends_at: editingSubscription.trial_ends_at ? new Date(editingSubscription.trial_ends_at).toISOString() : null,
        notes: editingSubscription.notes
      });
      toast.success(t.subscriptionUpdated);
      setShowEditSubscriptionForm(false);
      setEditingSubscription(null);
      loadData();
    } catch (error) {
      toast.error('Error al actualizar suscripción');
    }
  };

  const handleDeleteSubscription = async (subscriptionId) => {
    if (!window.confirm(t.confirmDelete)) return;
    
    try {
      await axios.delete(`/subscriptions/company/${subscriptionId}`);
      toast.success(t.subscriptionDeleted);
      loadData();
    } catch (error) {
      toast.error('Error al eliminar suscripción');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#1A4D5C]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <CreditCard className="h-6 w-6 text-[#1A4D5C]" />
          {t.title}
        </h2>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        <button
          onClick={() => setActiveTab('plans')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'plans' 
              ? 'text-[#1A4D5C] border-b-2 border-[#1A4D5C]' 
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Settings className="h-4 w-4 inline mr-2" />
          {t.plans}
        </button>
        <button
          onClick={() => setActiveTab('companies')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'companies' 
              ? 'text-[#1A4D5C] border-b-2 border-[#1A4D5C]' 
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Building className="h-4 w-4 inline mr-2" />
          {t.companies}
        </button>
      </div>

      {/* Plans Tab */}
      {activeTab === 'plans' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button 
              onClick={() => { setEditingPlan({}); setShowPlanForm(true); }}
              className="bg-[#1A4D5C] hover:bg-[#2A6A7C]"
            >
              <Plus className="h-4 w-4 mr-2" />
              {t.newPlan}
            </Button>
          </div>

          {/* Plan Form Modal */}
          {showPlanForm && (
            <PlanForm 
              plan={editingPlan}
              t={t}
              onSave={handleSavePlan}
              onCancel={() => { setShowPlanForm(false); setEditingPlan(null); }}
            />
          )}

          {/* Plans Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {plans.map(plan => (
              <PlanCard 
                key={plan.id}
                plan={plan}
                t={t}
                onEdit={() => { setEditingPlan(plan); setShowPlanForm(true); }}
                onDelete={() => handleDeletePlan(plan.id)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Companies Tab */}
      {activeTab === 'companies' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button 
              onClick={() => setShowAssignForm(true)}
              className="bg-[#1A4D5C] hover:bg-[#2A6A7C]"
            >
              <Plus className="h-4 w-4 mr-2" />
              {t.assignSubscription}
            </Button>
          </div>

          {/* Assign Form Modal */}
          {showAssignForm && (
            <Card className="border-2 border-[#1A4D5C]">
              <CardHeader>
                <CardTitle>{t.assignSubscription}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">{t.company}</label>
                    <select
                      value={assignFormData.empresa_id}
                      onChange={(e) => setAssignFormData({...assignFormData, empresa_id: e.target.value})}
                      className="w-full p-2 border rounded"
                    >
                      <option value="">Seleccionar empresa...</option>
                      {companies.map(c => (
                        <option key={c.id} value={c.id}>{c.nombre}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">{t.plan}</label>
                    <select
                      value={assignFormData.plan_id}
                      onChange={(e) => setAssignFormData({...assignFormData, plan_id: e.target.value})}
                      className="w-full p-2 border rounded"
                    >
                      <option value="">Seleccionar plan...</option>
                      {plans.filter(p => p.is_active).map(p => (
                        <option key={p.id} value={p.id}>{p.name} - ${p.price_monthly}/mes</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">{t.startDate}</label>
                    <Input
                      type="date"
                      value={assignFormData.start_date}
                      onChange={(e) => setAssignFormData({...assignFormData, start_date: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">{t.endDate} (opcional)</label>
                    <Input
                      type="date"
                      value={assignFormData.end_date}
                      onChange={(e) => setAssignFormData({...assignFormData, end_date: e.target.value})}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={assignFormData.is_trial}
                      onCheckedChange={(checked) => setAssignFormData({...assignFormData, is_trial: checked})}
                    />
                    <label className="text-sm">{t.trial}</label>
                  </div>
                  {assignFormData.is_trial && (
                    <div>
                      <label className="block text-sm font-medium mb-1">{t.trialEnds}</label>
                      <Input
                        type="date"
                        value={assignFormData.trial_ends_at}
                        onChange={(e) => setAssignFormData({...assignFormData, trial_ends_at: e.target.value})}
                      />
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">{t.notes}</label>
                  <Input
                    value={assignFormData.notes}
                    onChange={(e) => setAssignFormData({...assignFormData, notes: e.target.value})}
                    placeholder="Notas adicionales..."
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowAssignForm(false)}>
                    {t.cancel}
                  </Button>
                  <Button 
                    onClick={handleAssignSubscription}
                    disabled={!assignFormData.empresa_id || !assignFormData.plan_id}
                    className="bg-[#1A4D5C] hover:bg-[#2A6A7C]"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {t.save}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Company Subscriptions Table */}
          <Card>
            <CardContent className="p-0">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">{t.company}</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">{t.plan}</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">{t.evaluationsUsed}</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">{t.status}</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">{t.endDate}</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">{t.actions}</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {companySubscriptions.map(sub => (
                    <tr key={sub.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-medium">{sub.empresa_nombre}</div>
                        <div className="text-xs text-gray-500">{sub.employee_count} empleados</div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge className="bg-[#1A4D5C]">{sub.plan_name}</Badge>
                        {sub.is_trial && <Badge variant="outline" className="ml-1">Trial</Badge>}
                      </td>
                      <td className="px-4 py-3">
                        {sub.evaluations_used}
                      </td>
                      <td className="px-4 py-3">
                        {sub.is_active ? (
                          <Badge className="bg-green-100 text-green-700">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            {t.active}
                          </Badge>
                        ) : (
                          <Badge variant="destructive">
                            <X className="h-3 w-3 mr-1" />
                            {t.inactive}
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {sub.end_date ? new Date(sub.end_date).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEditSubscription(sub)}
                            data-testid={`edit-subscription-${sub.id}`}
                          >
                            <Edit className="h-3 w-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleDeleteSubscription(sub.id)}
                            data-testid={`delete-subscription-${sub.id}`}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {companySubscriptions.length === 0 && (
                    <tr>
                      <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                        No hay suscripciones registradas
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>

          {/* Edit Subscription Modal */}
          {showEditSubscriptionForm && editingSubscription && (
            <Card className="mt-4 border-2 border-[#1A4D5C]">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Edit className="h-5 w-5" />
                  {t.editSubscription}: {editingSubscription.empresa_nombre}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">{t.plan}</label>
                    <Select
                      value={editingSubscription.plan_id}
                      onValueChange={(value) => setEditingSubscription({...editingSubscription, plan_id: value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar plan" />
                      </SelectTrigger>
                      <SelectContent>
                        {plans.map(p => (
                          <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">{t.status}</label>
                    <Select
                      value={editingSubscription.is_active ? 'active' : 'inactive'}
                      onValueChange={(value) => setEditingSubscription({...editingSubscription, is_active: value === 'active'})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">{t.active}</SelectItem>
                        <SelectItem value="inactive">{t.inactive}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">{t.startDate}</label>
                    <Input
                      type="date"
                      value={editingSubscription.start_date}
                      onChange={(e) => setEditingSubscription({...editingSubscription, start_date: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">{t.endDate}</label>
                    <Input
                      type="date"
                      value={editingSubscription.end_date}
                      onChange={(e) => setEditingSubscription({...editingSubscription, end_date: e.target.value})}
                    />
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={editingSubscription.is_trial}
                      onChange={(e) => setEditingSubscription({...editingSubscription, is_trial: e.target.checked})}
                      className="rounded"
                    />
                    <span className="text-sm">{t.trial}</span>
                  </label>
                  {editingSubscription.is_trial && (
                    <div className="flex-1">
                      <Input
                        type="date"
                        value={editingSubscription.trial_ends_at}
                        onChange={(e) => setEditingSubscription({...editingSubscription, trial_ends_at: e.target.value})}
                        placeholder={t.trialEnds}
                      />
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">{t.notes}</label>
                  <textarea
                    value={editingSubscription.notes}
                    onChange={(e) => setEditingSubscription({...editingSubscription, notes: e.target.value})}
                    className="w-full border rounded-lg p-2 text-sm"
                    rows="2"
                    placeholder="Notas adicionales..."
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => {
                    setShowEditSubscriptionForm(false);
                    setEditingSubscription(null);
                  }}>
                    {t.cancel}
                  </Button>
                  <Button 
                    onClick={handleUpdateSubscription}
                    className="bg-[#1A4D5C] hover:bg-[#2A6A7C]"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {t.save}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

// Plan Card Component
const PlanCard = ({ plan, t, onEdit, onDelete }) => {
  const [expanded, setExpanded] = useState(false);

  const modules = [
    { key: 'module_culture', label: t.moduleLabels.culture },
    { key: 'module_personality', label: t.moduleLabels.personality },
    { key: 'module_integrated_report', label: t.moduleLabels.integrated_report },
    { key: 'module_hr_indicators', label: t.moduleLabels.hr_indicators },
    { key: 'module_alerts', label: t.moduleLabels.alerts },
    { key: 'module_action_plans', label: t.moduleLabels.action_plans },
    { key: 'module_evaluation_periods', label: t.moduleLabels.evaluation_periods },
    { key: 'module_profile_evaluation', label: t.moduleLabels.profile_evaluation },
    { key: 'module_corporate_identity', label: t.moduleLabels.corporate_identity },
    { key: 'module_pdf_export', label: t.moduleLabels.pdf_export },
    { key: 'module_candidate_evaluation', label: t.moduleLabels.candidate_evaluation },
    { key: 'module_nom035', label: t.moduleLabels.nom035 },
    { key: 'module_orgchart', label: t.moduleLabels.orgchart },
    { key: 'module_dashboard_central', label: t.moduleLabels.dashboard_central },
    { key: 'module_evolution_impact', label: t.moduleLabels.evolution_impact },
    { key: 'module_pulse_surveys', label: t.moduleLabels.pulse_surveys },
    { key: 'module_strategic_plans', label: t.moduleLabels.strategic_plans },
    { key: 'module_evaluation_cycles', label: t.moduleLabels.evaluation_cycles },
    { key: 'module_company_profile', label: t.moduleLabels.company_profile },
    { key: 'module_compatibility_4d', label: t.moduleLabels.compatibility_4d }
  ];

  const features = [
    { key: 'feature_basic_reports', label: t.featureLabels.basic_reports },
    { key: 'feature_advanced_reports', label: t.featureLabels.advanced_reports },
    { key: 'feature_api_access', label: t.featureLabels.api_access },
    { key: 'feature_priority_support', label: t.featureLabels.priority_support }
  ];

  return (
    <Card className={`${!plan.is_active ? 'opacity-60' : ''}`}>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              {plan.name}
              {!plan.is_active && <Badge variant="secondary">{t.inactive}</Badge>}
            </CardTitle>
            <CardDescription>{plan.description}</CardDescription>
          </div>
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" onClick={onEdit}>
              <Edit className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={onDelete}>
              <Trash2 className="h-4 w-4 text-red-500" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Pricing */}
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold text-[#1A4D5C]">
              ${plan.price_monthly?.toLocaleString()}
            </span>
            <span className="text-gray-500">/mes</span>
          </div>

          {/* Limits */}
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-gray-400" />
              <span>
                {plan.max_employees === -1 ? t.unlimited : `${plan.max_employees} empleados`}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-gray-400" />
              <span>
                {plan.max_evaluations === -1 ? t.unlimited : `${plan.max_evaluations} evaluaciones`}
              </span>
            </div>
          </div>

          {/* Expandable Modules */}
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-1 text-sm text-[#1A4D5C] hover:underline"
          >
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            {t.modules} & {t.features}
          </button>

          {expanded && (
            <div className="space-y-3 pt-2 border-t">
              <div>
                <p className="text-xs font-medium text-gray-500 mb-2">{t.modules}</p>
                <div className="flex flex-wrap gap-1">
                  {modules.map(m => (
                    <Badge 
                      key={m.key} 
                      variant={plan[m.key] ? "default" : "outline"}
                      className={`text-xs ${plan[m.key] ? 'bg-green-100 text-green-700' : 'text-gray-400'}`}
                    >
                      {plan[m.key] ? <Check className="h-3 w-3 mr-1" /> : <X className="h-3 w-3 mr-1" />}
                      {m.label}
                    </Badge>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 mb-2">{t.features}</p>
                <div className="flex flex-wrap gap-1">
                  {features.map(f => (
                    <Badge 
                      key={f.key}
                      variant={plan[f.key] ? "default" : "outline"}
                      className={`text-xs ${plan[f.key] ? 'bg-blue-100 text-blue-700' : 'text-gray-400'}`}
                    >
                      {plan[f.key] ? <Check className="h-3 w-3 mr-1" /> : <X className="h-3 w-3 mr-1" />}
                      {f.label}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Plan Form Component
const PlanForm = ({ plan, t, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: plan?.name || '',
    description: plan?.description || '',
    price_monthly: plan?.price_monthly || 0,
    price_annual: plan?.price_annual || null,
    max_evaluations: plan?.max_evaluations ?? -1,
    max_employees: plan?.max_employees ?? -1,
    module_culture: plan?.module_culture ?? true,
    module_personality: plan?.module_personality ?? true,
    module_integrated_report: plan?.module_integrated_report ?? true,
    module_hr_indicators: plan?.module_hr_indicators ?? true,
    module_alerts: plan?.module_alerts ?? true,
    module_action_plans: plan?.module_action_plans ?? false,
    module_evaluation_periods: plan?.module_evaluation_periods ?? false,
    module_profile_evaluation: plan?.module_profile_evaluation ?? false,
    module_corporate_identity: plan?.module_corporate_identity ?? false,
    module_pdf_export: plan?.module_pdf_export ?? true,
    module_candidate_evaluation: plan?.module_candidate_evaluation ?? true,
    module_nom035: plan?.module_nom035 ?? false,
    module_orgchart: plan?.module_orgchart ?? false,
    module_dashboard_central: plan?.module_dashboard_central ?? true,
    module_evolution_impact: plan?.module_evolution_impact ?? false,
    module_pulse_surveys: plan?.module_pulse_surveys ?? false,
    module_strategic_plans: plan?.module_strategic_plans ?? false,
    module_evaluation_cycles: plan?.module_evaluation_cycles ?? false,
    module_company_profile: plan?.module_company_profile ?? false,
    module_compatibility_4d: plan?.module_compatibility_4d ?? false,
    feature_basic_reports: plan?.feature_basic_reports ?? true,
    feature_advanced_reports: plan?.feature_advanced_reports ?? false,
    feature_api_access: plan?.feature_api_access ?? false,
    feature_priority_support: plan?.feature_priority_support ?? false,
    is_active: plan?.is_active ?? true,
    sort_order: plan?.sort_order ?? 0
  });

  const modules = [
    { key: 'module_culture', label: t.moduleLabels.culture },
    { key: 'module_personality', label: t.moduleLabels.personality },
    { key: 'module_integrated_report', label: t.moduleLabels.integrated_report },
    { key: 'module_hr_indicators', label: t.moduleLabels.hr_indicators },
    { key: 'module_alerts', label: t.moduleLabels.alerts },
    { key: 'module_action_plans', label: t.moduleLabels.action_plans },
    { key: 'module_evaluation_periods', label: t.moduleLabels.evaluation_periods },
    { key: 'module_profile_evaluation', label: t.moduleLabels.profile_evaluation },
    { key: 'module_corporate_identity', label: t.moduleLabels.corporate_identity },
    { key: 'module_pdf_export', label: t.moduleLabels.pdf_export },
    { key: 'module_candidate_evaluation', label: t.moduleLabels.candidate_evaluation },
    { key: 'module_nom035', label: t.moduleLabels.nom035 },
    { key: 'module_orgchart', label: t.moduleLabels.orgchart },
    { key: 'module_dashboard_central', label: t.moduleLabels.dashboard_central },
    { key: 'module_evolution_impact', label: t.moduleLabels.evolution_impact },
    { key: 'module_pulse_surveys', label: t.moduleLabels.pulse_surveys },
    { key: 'module_strategic_plans', label: t.moduleLabels.strategic_plans },
    { key: 'module_evaluation_cycles', label: t.moduleLabels.evaluation_cycles },
    { key: 'module_company_profile', label: t.moduleLabels.company_profile },
    { key: 'module_compatibility_4d', label: t.moduleLabels.compatibility_4d }
  ];

  const features = [
    { key: 'feature_basic_reports', label: t.featureLabels.basic_reports },
    { key: 'feature_advanced_reports', label: t.featureLabels.advanced_reports },
    { key: 'feature_api_access', label: t.featureLabels.api_access },
    { key: 'feature_priority_support', label: t.featureLabels.priority_support }
  ];

  return (
    <Card className="border-2 border-[#1A4D5C]">
      <CardHeader>
        <CardTitle>{plan?.id ? 'Editar Plan' : t.newPlan}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Basic Info */}
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">{t.planName}</label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t.description}</label>
            <Input
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t.priceMonthly}</label>
            <Input
              type="number"
              value={formData.price_monthly}
              onChange={(e) => setFormData({...formData, price_monthly: parseFloat(e.target.value) || 0})}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t.priceAnnual}</label>
            <Input
              type="number"
              value={formData.price_annual || ''}
              onChange={(e) => setFormData({...formData, price_annual: e.target.value ? parseFloat(e.target.value) : null})}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t.maxEmployees} (-1 = {t.unlimited})</label>
            <Input
              type="number"
              value={formData.max_employees}
              onChange={(e) => setFormData({...formData, max_employees: parseInt(e.target.value) || -1})}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t.maxEvaluations} (-1 = {t.unlimited})</label>
            <Input
              type="number"
              value={formData.max_evaluations}
              onChange={(e) => setFormData({...formData, max_evaluations: parseInt(e.target.value) || -1})}
            />
          </div>
        </div>

        {/* Modules */}
        <div>
          <h4 className="font-medium mb-3">{t.modules}</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {modules.map(m => (
              <div key={m.key} className="flex items-center gap-2">
                <Switch
                  checked={formData[m.key]}
                  onCheckedChange={(checked) => setFormData({...formData, [m.key]: checked})}
                />
                <label className="text-sm">{m.label}</label>
              </div>
            ))}
          </div>
        </div>

        {/* Features */}
        <div>
          <h4 className="font-medium mb-3">{t.features}</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {features.map(f => (
              <div key={f.key} className="flex items-center gap-2">
                <Switch
                  checked={formData[f.key]}
                  onCheckedChange={(checked) => setFormData({...formData, [f.key]: checked})}
                />
                <label className="text-sm">{f.label}</label>
              </div>
            ))}
          </div>
        </div>

        {/* Active Status */}
        <div className="flex items-center gap-2">
          <Switch
            checked={formData.is_active}
            onCheckedChange={(checked) => setFormData({...formData, is_active: checked})}
          />
          <label className="text-sm font-medium">{t.active}</label>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={onCancel}>
            {t.cancel}
          </Button>
          <Button 
            onClick={() => onSave(formData)}
            className="bg-[#1A4D5C] hover:bg-[#2A6A7C]"
          >
            <Save className="h-4 w-4 mr-2" />
            {t.save}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default SubscriptionManagement;
